import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import SuperAdminDashboard from './components/dashboards/SuperAdminDashboard';
import SMTenderManagementDashboard from './components/dashboards/SMTenderManagementDashboard';
import SMProductManagerDashboard from './components/dashboards/SMProductManagerDashboard';
import SMFinanceDashboard from './components/dashboards/SMFinanceDashboard';
import SanbeeEmployeeDashboard from './components/dashboards/SanbeeEmployeeDashboard';
import AnalyticsDashboard from './components/dashboards/AnalyticsDashboard';
import { checkAuth, logout as apiLogout, setSessionExpiredCallback } from './services/api';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import smLogo from './assets/SM_logo-removebg.png';
import './App.css';

// Session timeout in milliseconds (25 minutes)
const SESSION_TIMEOUT = 25 * 60 * 1000;

// Dashboard URL mapping
const DASHBOARD_ROUTES = {
  super_admin: '/admin',
  sm_tender_management: '/sm-tender-management',
  sm_product_manager: '/sm-product-manager',
  sm_finance: '/sm-finance',
  sanbee_employee: '/sanbee-employee',
  analytics: '/analytics'
};

// Get dashboard URL based on user role
const getDashboardUrl = (user) => {
  if (!user) return '/login';
  const role = user.dashboard || user.role;
  return DASHBOARD_ROUTES[role] || '/sanbee-employee';
};

// Protected Route Component - checks authorization
const ProtectedRoute = ({ user, allowedRoles, children, onLogout }) => {
  const location = useLocation();
  
  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  const userRole = user.dashboard || user.role;
  
  // Check if user has access to this route
  if (!allowedRoles.includes(userRole)) {
    // Redirect to user's authorized dashboard
    return <Navigate to={getDashboardUrl(user)} replace />;
  }
  
  // User is authorized - render the component
  return children;
};

// Unauthorized Access Page
const UnauthorizedPage = ({ user }) => {
  return (
    <div className="unauthorized-page">
      <div className="unauthorized-content">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
        <a href={getDashboardUrl(user)} className="back-btn">Go to My Dashboard</a>
      </div>
    </div>
  );
};

// Session Expired Modal Component
const SessionExpiredModal = ({ onRelogin }) => {
  return (
    <div className="session-modal-overlay">
      <div className="session-modal">
        <div className="session-modal-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        <h2>Session Expired</h2>
        <p>Your session has expired due to inactivity. Please log in again to continue.</p>
        <button onClick={onRelogin} className="session-relogin-btn">
          Log In Again
        </button>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  
  // Refs for session timeout
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    console.log('Session expired due to inactivity');
    setSessionExpired(true);
    setUser(null);
    localStorage.removeItem('ccms_user');
    
    // Clear the timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Only set timer if user is logged in
    if (user) {
      timeoutRef.current = setTimeout(() => {
        // Session expired due to inactivity
        handleSessionExpired();
      }, SESSION_TIMEOUT);
    }
  }, [user, handleSessionExpired]);

  // Set up the API session expired callback
  useEffect(() => {
    setSessionExpiredCallback(() => {
      if (!sessionExpired) {
        handleSessionExpired();
      }
    });
  }, [handleSessionExpired, sessionExpired]);

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user, resetInactivityTimer]);

  // Check authentication status on mount and when tab becomes visible
  const validateSession = useCallback(async () => {
    try {
      const response = await checkAuth();
      
      if (response.success && response.authenticated && response.user) {
        setUser(response.user);
        localStorage.setItem('ccms_user', JSON.stringify(response.user));
        setAuthError(null);
      } else {
        // Session not verified - logout automatically
        setUser(null);
        localStorage.removeItem('ccms_user');
      }
    } catch (error) {
      console.error('Session validation error:', error);
      // Session could not be verified (401, network error, etc.) - logout automatically
      setUser(null);
      localStorage.removeItem('ccms_user');
      if (error.status !== 401) {
        setAuthError('Unable to verify session. Please log in again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Validate session on mount
  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Re-validate session when user returns to the tab (e.g. session invalidated on server)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        checkAuth()
          .then((response) => {
            if (!response.success || !response.authenticated || !response.user) {
              setUser(null);
              localStorage.removeItem('ccms_user');
            } else {
              setUser(response.user);
              localStorage.setItem('ccms_user', JSON.stringify(response.user));
            }
          })
          .catch(() => {
            setUser(null);
            localStorage.removeItem('ccms_user');
          });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
    setAuthError(null);
    setSessionExpired(false);
    localStorage.setItem('ccms_user', JSON.stringify(userData));
    // Reset activity timer on login
    lastActivityRef.current = Date.now();
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('ccms_user');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  const handleRelogin = () => {
    setSessionExpired(false);
    // Navigate to login will happen automatically since user is null
  };

  if (isLoading) {
    return (
      <div className="app-loading">
        <img src={smLogo} alt="SM Systems" className="loading-logo" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <ThemeProvider>
      <ToastProvider>
      <div className="App">
        {/* Session Expired Modal */}
        {sessionExpired && (
          <SessionExpiredModal onRelogin={handleRelogin} />
        )}
        
        {authError && (
          <div className="auth-error-banner">
            {authError}
            <button onClick={() => setAuthError(null)}>Dismiss</button>
          </div>
        )}
        <Routes>
          {/* Login Route */}
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={getDashboardUrl(user)} replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />

          {/* Super Admin Dashboard - /admin */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute 
                user={user} 
                allowedRoles={['super_admin']}
                onLogout={handleLogout}
              >
                <SuperAdminDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* SM Tender Management Dashboard - /sm-tender-management */}
          <Route
            path="/sm-tender-management/*"
            element={
              <ProtectedRoute 
                user={user} 
                allowedRoles={['sm_tender_management', 'super_admin']}
                onLogout={handleLogout}
              >
                <SMTenderManagementDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* SM Product Manager Dashboard - /sm-product-manager */}
          <Route
            path="/sm-product-manager/*"
            element={
              <ProtectedRoute 
                user={user} 
                allowedRoles={['sm_product_manager', 'super_admin']}
                onLogout={handleLogout}
              >
                <SMProductManagerDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* SM Finance Dashboard - /sm-finance */}
          <Route
            path="/sm-finance/*"
            element={
              <ProtectedRoute 
                user={user} 
                allowedRoles={['sm_finance', 'super_admin']}
                onLogout={handleLogout}
              >
                <SMFinanceDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* Sanbee Employee Dashboard - /sanbee-employee */}
          <Route
            path="/sanbee-employee/*"
            element={
              <ProtectedRoute 
                user={user} 
                allowedRoles={['sanbee_employee', 'super_admin']}
                onLogout={handleLogout}
              >
                <SanbeeEmployeeDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* Analytics Dashboard - /analytics */}
          <Route
            path="/analytics/*"
            element={
              <ProtectedRoute 
                user={user} 
                allowedRoles={['analytics', 'super_admin']}
                onLogout={handleLogout}
              >
                <AnalyticsDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* Unauthorized Access */}
          <Route
            path="/unauthorized"
            element={<UnauthorizedPage user={user} />}
          />

          {/* Legacy /dashboard route - redirect to appropriate dashboard */}
          <Route
            path="/dashboard"
            element={<Navigate to={getDashboardUrl(user)} replace />}
          />

          {/* Root Route */}
          <Route
            path="/"
            element={<Navigate to={user ? getDashboardUrl(user) : "/login"} replace />}
          />

          {/* Catch all - redirect to appropriate page */}
          <Route
            path="*"
            element={<Navigate to={user ? getDashboardUrl(user) : "/login"} replace />}
          />
        </Routes>
      </div>
      </ToastProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
